import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, ShieldCheck } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { AnalysisDraft } from "@/components/analysis/AnalysisDraft";
import { ApprovalEditor } from "@/components/analysis/ApprovalEditor";

export const AnalysisDesk = ({ meetingId, race, onUpdate }) => {
  const api = useApi();
  const a = race.analysis;
  const [shortlist, setShortlist] = useState([]);
  const [extra, setExtra] = useState("");
  const generating = a?.job?.state === "generating";
  const busy = generating;
  useEffect(() => { setShortlist(a?.shortlist || []); setExtra(a?.extra || ""); }, [race.number, a?.generated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!generating) return;
    const startedAt = Date.now();
    const t = setInterval(async () => {
      if (Date.now() - startedAt > 6 * 60 * 1000) { clearInterval(t); toast.error("Analysis is taking too long. Refresh and try again."); return; }
      try {
        const m = await api("get", `/admin/meetings/${meetingId}`);
        const next = m.races.find((r) => r.number === race.number)?.analysis;
        if (next?.job?.state === "generating") return;
        onUpdate(next);
        next?.job?.state === "error" ? toast.error(next.job.error) : toast.success("Draft analysis ready for review");
      } catch (e) { toast.error(errMsg(e)); }
    }, 4000);
    return () => clearInterval(t);
  }, [generating, meetingId, race.number, api, onUpdate]);

  const toggle = (n) => setShortlist((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const generate = async () => {
    if (a?.status === "published" && !window.confirm("Tips for this race are live. Re-running creates a new draft for review; the live tips stay up until you publish again. Continue?")) return;
    try { onUpdate(await api("post", `/admin/meetings/${meetingId}/races/${race.number}/analysis`, { shortlist, extra })); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-amber-500/30 p-5 space-y-5" data-testid="analysis-desk">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-amber-400" />
        <h3 className="font-display font-bold text-lg">Analysis Desk <span className="text-xs font-normal text-slate-400">— admin only, nothing is public until you publish</span></h3>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">1. Your shortlist</p>
        <div className="flex flex-wrap gap-2">
          {race.runners.filter((r) => !r.scratched).map((r) => (
            <button key={r.number} data-testid={`shortlist-toggle-${r.number}`} onClick={() => toggle(r.number)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${shortlist.includes(r.number) ? "bg-blue-600 border-blue-500" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
              {r.number}. {r.horse}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">2. Extra verified data (optional)</p>
        <textarea data-testid="analysis-extra-input" rows={4} value={extra} onChange={(e) => setExtra(e.target.value)}
          placeholder="Paste sectionals, speed figures, current odds, scratchings, gear changes…"
          className="w-full bg-[#0B101D] border border-white/10 rounded-xl p-3 text-sm font-mono outline-none focus:border-blue-500" />
      </div>
      <button data-testid="analysis-generate-button" onClick={generate} disabled={busy || !shortlist.length}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 font-semibold text-sm disabled:opacity-50 transition-colors">
        <Sparkles className={`w-4 h-4 ${busy ? "animate-pulse" : ""}`} />{busy ? "Analysing… (1-3 minutes, you can keep browsing this page)" : a?.draft ? "Re-run analysis" : "Run analysis"}
      </button>
      {a?.job?.state === "error" && <p className="text-sm text-rose-300" data-testid="analysis-error">{a.job.error}</p>}
      {a?.draft && (
        <>
          <p className="text-[11px] uppercase tracking-widest text-slate-500">3. Review the draft</p>
          <AnalysisDraft d={a.draft} />
          <p className="text-[11px] uppercase tracking-widest text-slate-500">4. Approve</p>
          <ApprovalEditor meetingId={meetingId} race={race} analysis={a} onSaved={onUpdate} locked={generating} />
        </>
      )}
    </section>
  );
};
